import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

import FloatingAdminMenu from "./FloatMenu";
import AtomBackground from "./Atombackground";

import {
  FiLock, FiEye, FiEyeOff, FiLogOut,
  FiUser, FiMail, FiPhone, FiRefreshCw,
  FiAlertCircle, FiMenu,
} from "react-icons/fi";

import { FaWhatsapp } from "react-icons/fa";

/* ─────────────────────────────────────
   API & AUTH
───────────────────────────────────── */
const API      = "https://www.medicinacondante.com/mentorias.php";
const PASSWORD = "dante.admin";

/* ─────────────────────────────────────
   ESTILOS GLOBALES
───────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200;0,300;0,400;0,600;0,700;0,800;1,300&display=swap');

    :root {
      --gold:      #C9A84C;
      --gold-dim:  rgba(201,168,76,0.55);
      --gold-sub:  rgba(201,168,76,0.08);
      --gold-brd:  rgba(201,168,76,0.20);
      --bg:        #0A0906;
      --bg-2:      #0F0E0B;
      --bg-3:      #141208;
      --line:      rgba(201,168,76,0.12);
      --ink:       #EDE0C8;
      --ink-2:     rgba(237,224,200,0.62);
      --ink-3:     rgba(237,224,200,0.32);
      --green:     #6DBF8F;
      --red:       #E07A7A;
    }

    *, *::before, *::after { box-sizing: border-box; }

    body {
      background: var(--bg);
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    ::placeholder {
      color: var(--ink-3);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 300;
      font-style: italic;
    }

    @keyframes adm-spin {
      to { transform: rotate(360deg); }
    }
    .adm-spin { animation: adm-spin 0.9s linear infinite; }

    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--bg-2); }
    ::-webkit-scrollbar-thumb { background: var(--gold-brd); border-radius: 2px; }
  `}</style>
);

/* ─────────────────────────────────────
   MOTION VARIANTS
───────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show:   (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: i * 0.07 },
  }),
};

/* ─────────────────────────────────────
   SECTION LABEL
───────────────────────────────────── */
const SectionLabel = ({ children, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span className="inline-block w-5 h-px" style={{ background: "var(--gold)", opacity: 0.7 }} />
    <span style={{ fontWeight: 700, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink-3)" }}>
      {children}
    </span>
  </div>
);

/* ─────────────────────────────────────
   MENTORIA CARD
───────────────────────────────────── */
const MentoriaCard = ({ item, index, onWhatsApp }) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    animate="show"
    className="relative overflow-hidden"
    style={{ background: "var(--bg-3)", border: "1px solid var(--line)" }}
  >
    {/* Accent top */}
    <div
      className="absolute top-0 left-6 right-6 h-px"
      style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.28), transparent)" }}
    />

    <div style={{ padding: "clamp(20px,3vw,28px)" }}>

      {/* Nombre */}
      <div className="flex items-start gap-3 mb-5 pb-5" style={{ borderBottom: "1px solid var(--line)" }}>
        {/* Avatar inicial */}
        <div
          className="flex-shrink-0 flex items-center justify-center"
          style={{ width: 40, height: 40, background: "var(--gold-sub)", border: "1px solid var(--gold-brd)", color: "var(--gold)", fontSize: 14, fontWeight: 700 }}
        >
          {(item.nombre || "?")[0].toUpperCase()}
        </div>
        <div className="min-w-0">
          <p style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
            {item.nombre} {item.apellido}
          </p>
          <p style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)", marginTop: 3 }}>
            Solicitud de mentoría
          </p>
        </div>
      </div>

      {/* Datos de contacto */}
      <div className="flex flex-col gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--line)", flexShrink: 0, color: "var(--gold-dim)" }}>
            <FiMail size={12} />
          </div>
          <p style={{ fontSize: 12, fontWeight: 300, color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.email}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--line)", flexShrink: 0, color: "var(--gold-dim)" }}>
            <FiPhone size={12} />
          </div>
          <p style={{ fontSize: 12, fontWeight: 300, color: "var(--ink-2)" }}>
            {item.telefono}
          </p>
        </div>
      </div>

      {/* Objetivo */}
      {item.objetivo && (
        <div
          className="mb-6"
          style={{ borderLeft: "2px solid rgba(201,168,76,0.25)", paddingLeft: 14 }}
        >
          <p style={{ fontSize: 12, fontWeight: 300, color: "var(--ink-2)", lineHeight: 1.85, fontStyle: "italic" }}>
            {item.objetivo}
          </p>
        </div>
      )}

      {/* Acción: solo WhatsApp */}
      <motion.button
        whileHover={{ opacity: 0.85, x: 2 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => onWhatsApp(item.telefono, item.nombre)}
        className="w-full flex items-center justify-center gap-2.5"
        style={{
          padding: "11px 0",
          background: "rgba(109,191,143,0.10)",
          border: "1px solid rgba(109,191,143,0.22)",
          color: "var(--green)",
          cursor: "pointer",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          transition: "opacity 0.2s",
        }}
      >
        <FaWhatsapp size={15} />
        Contactar por WhatsApp
      </motion.button>
    </div>
  </motion.div>
);

/* ═══════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════ */
const AdminMentorias = () => {
  const [data,          setData]          = useState([]);
  const [isAuth,        setIsAuth]        = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword,  setShowPassword]  = useState(false);
  const [errorAuth,     setErrorAuth]     = useState("");
  const [loading,       setLoading]       = useState(false);
  const [showMenu,      setShowMenu]      = useState(false);

  /* ── Auth ── */
  useEffect(() => {
    if (localStorage.getItem("admin_auth") === "true") setIsAuth(true);
  }, []);

  const handleLogin = () => {
    if (passwordInput === PASSWORD) {
      setIsAuth(true);
      localStorage.setItem("admin_auth", "true");
      setErrorAuth("");
    } else {
      setErrorAuth("Contraseña incorrecta");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_auth");
    setIsAuth(false);
  };

  /* ── Fetch ── */
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API);
      setData(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { if (isAuth) fetchData(); }, [isAuth]);

  /* ── WhatsApp ── */
  const goToWhatsApp = (telefono, nombre) => {
    const clean = telefono.replace(/\D/g, "");
    window.open(`https://wa.me/${clean}?text=Hola ${nombre}, te contacto por tu solicitud de mentoría ✨`, "_blank");
  };

  /* ════════════════════════════
     RENDER
  ════════════════════════════ */
  return (
    <>
      <GlobalStyles />

      <div
        className="relative min-h-screen overflow-x-hidden"
        style={{ background: "var(--bg)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        {/* Radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 15% 0%, rgba(201,168,76,0.07) 0%, transparent 55%)" }}
        />

        {/* Top line */}
        <div
          className="fixed top-0 left-0 right-0 h-px z-50"
          style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)" }}
        />

        {/* ─────── LOGIN ─────── */}
        <AnimatePresence>
          {!isAuth && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[99999] flex items-center justify-center px-4"
            >
              <div
                className="absolute inset-0"
                style={{ background: "rgba(10,9,6,0.96)", backdropFilter: "blur(14px)" }}
              />

              {/* AtomBackground solo en el login */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <AtomBackground />
              </div>

              <motion.div
                initial={{ scale: 0.88, y: 28, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.88, y: 28, opacity: 0 }}
                transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 w-full max-w-sm shadow-2xl"
                style={{ background: "var(--bg-3)", border: "1px solid var(--line)" }}
              >
                <div
                  className="absolute top-0 left-8 right-8 h-px"
                  style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)" }}
                />

                <div className="p-8 sm:p-10">
                  <div className="flex items-center gap-4 mb-8">
                    <div
                      className="w-11 h-11 flex items-center justify-center flex-shrink-0"
                      style={{ border: "1px solid var(--line)", background: "var(--gold-sub)", color: "var(--gold)" }}
                    >
                      <FiLock size={16} />
                    </div>
                    <div>
                      <SectionLabel>Acceso restringido</SectionLabel>
                      <h2
                        className="mt-1"
                        style={{ fontSize: "clamp(1rem,3vw,1.2rem)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.01em" }}
                      >
                        IDM Mentorías Admin
                      </h2>
                    </div>
                  </div>

                  <div className="h-px mb-6" style={{ background: "var(--line)" }} />

                  <div className="relative mb-3">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                      placeholder="Contraseña de acceso"
                      style={{
                        background: "var(--bg-2)", color: "var(--ink)",
                        border: "1px solid var(--line)", outline: "none",
                        width: "100%", height: 48, padding: "0 48px 0 16px",
                        fontSize: 14, fontWeight: 300, borderRadius: 0,
                      }}
                    />
                    <button
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2"
                      style={{ background: "none", border: "none", color: "var(--gold-dim)", cursor: "pointer" }}
                    >
                      {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    </button>
                  </div>

                  <AnimatePresence>
                    {errorAuth && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="flex items-center gap-2 mb-3"
                        style={{ fontSize: 12, color: "var(--red)" }}
                      >
                        <FiAlertCircle size={11} /> {errorAuth}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <motion.button
                    whileHover={{ opacity: 0.86 }} whileTap={{ scale: 0.97 }}
                    onClick={handleLogin}
                    className="w-full"
                    style={{
                      padding: "14px 0", background: "var(--gold)", color: "#0A0906",
                      fontSize: 12, fontWeight: 700, letterSpacing: "0.12em",
                      textTransform: "uppercase", border: "none", cursor: "pointer",
                    }}
                  >
                    Ingresar
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Menú flotante */}
        {isAuth && <FloatingAdminMenu showMenu={showMenu} setShowMenu={setShowMenu} />}

        {/* ─────── PANEL ─────── */}
        {isAuth && (
          <div
            className="relative z-10 w-full max-w-6xl mx-auto"
            style={{ padding: "clamp(80px,10vw,120px) clamp(16px,5vw,48px) 96px" }}
          >
            {/* HEADER */}
            <motion.div
              variants={fadeUp} initial="hidden" animate="show"
              className="mb-12"
            >
              <SectionLabel className="mb-3">Panel de administración</SectionLabel>
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <h1 style={{ fontSize: "clamp(1.75rem,4vw,2.8rem)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.02em", lineHeight: 1.04 }}>
                    Panel de <em style={{ color: "var(--gold)", fontStyle: "normal" }}>Mentorías</em>
                  </h1>
                  <p className="mt-2" style={{ fontSize: 13, fontWeight: 300, color: "var(--ink-3)", fontStyle: "italic" }}>
                    Solicitudes recibidas de estudiantes IDM
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  {/* Contador */}
                  {data.length > 0 && (
                    <div style={{ textAlign: "right" }}>
                      <SectionLabel className="mb-1">Solicitudes</SectionLabel>
                      <p style={{ fontSize: 14, fontWeight: 700, color: "var(--gold)" }}>
                        {data.length}
                      </p>
                    </div>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.93 }}
                    onClick={fetchData}
                    className="w-10 h-10 flex items-center justify-center"
                    style={{ border: "1px solid var(--line)", color: "var(--gold)", background: "transparent", cursor: "pointer" }}
                  >
                    <FiRefreshCw size={14} className={loading ? "adm-spin" : ""} />
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.93 }}
                    onClick={handleLogout}
                    className="w-10 h-10 flex items-center justify-center"
                    style={{ border: "1px solid rgba(224,122,122,0.2)", color: "var(--red)", background: "transparent", cursor: "pointer" }}
                  >
                    <FiLogOut size={14} />
                  </motion.button>
                </div>
              </div>

              {/* Separador */}
              <motion.div
                className="h-px mt-6"
                style={{ background: "linear-gradient(90deg, var(--gold) 0%, transparent 100%)", opacity: 0.14 }}
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              />
            </motion.div>

            {/* LOADING */}
            <AnimatePresence>
              {loading && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex flex-col items-center py-32 gap-5"
                >
                  <div
                    className="adm-spin"
                    style={{ width: 36, height: 36, borderRadius: "50%", border: "1px solid var(--line)", borderTopColor: "var(--gold)" }}
                  />
                  <p style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--gold-dim)" }}>
                    Cargando solicitudes
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* EMPTY */}
            {!loading && data.length === 0 && (
              <motion.div
                variants={fadeUp} initial="hidden" animate="show"
                className="flex flex-col items-center justify-center py-32 gap-4"
              >
                <div
                  className="w-14 h-14 flex items-center justify-center"
                  style={{ border: "1px solid var(--line)", color: "var(--gold-dim)" }}
                >
                  <FiUser size={20} />
                </div>
                <p style={{ fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--ink-3)" }}>
                  No hay solicitudes por el momento
                </p>
                <div className="h-px w-20" style={{ background: "var(--line)" }} />
              </motion.div>
            )}

            {/* GRID DE CARDS */}
            {!loading && data.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-5">
                {data.map((item, i) => (
                  <MentoriaCard
                    key={item.id}
                    item={item}
                    index={i}
                    onWhatsApp={goToWhatsApp}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AdminMentorias;